import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'

const KEY_ID = 14

type Field = 'description' | 'short_desc'

const TOOLS: { label: string; icon: string; wrap: [string, string] }[] = [
  { label: 'Tiêu đề', icon: 'fa-heading', wrap: ['<h2>', '</h2>'] },
  { label: 'Đậm', icon: 'fa-bold', wrap: ['<strong>', '</strong>'] },
  { label: 'Nghiêng', icon: 'fa-italic', wrap: ['<em>', '</em>'] },
  { label: 'Đoạn', icon: 'fa-paragraph', wrap: ['<p>', '</p>'] },
  { label: 'Danh sách', icon: 'fa-list-ul', wrap: ['<ul>\n  <li>', '</li>\n</ul>'] },
  { label: 'Link', icon: 'fa-link', wrap: ['<a href="https://">', '</a>'] },
  { label: 'Ảnh', icon: 'fa-image', wrap: ['<img src="https://', '" alt="" />'] },
]

export function AdminKeyIntroPage() {
  const { user, loading } = useAuth()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [shortDesc, setShortDesc] = useState('')
  const [field, setField] = useState<Field>('description')
  const [view, setView] = useState<'edit' | 'preview'>('edit')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [err, setErr] = useState('')
  const [dirty, setDirty] = useState(false)
  const areaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (user?.role !== 'admin') return
    api
      .adminKeyIntro(KEY_ID)
      .then((r) => {
        setName(r.intro.name)
        setDescription(r.intro.description)
        setShortDesc(r.intro.short_desc)
      })
      .catch((e) => setErr(e.message))
  }, [user])

  useEffect(() => {
    if (!dirty) return
    const onLeave = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', onLeave)
    return () => window.removeEventListener('beforeunload', onLeave)
  }, [dirty])

  if (loading) return <p className="muted">Đang tải…</p>
  if (!user) return <Navigate to="/login" replace />
  if (user.role !== 'admin') return <p className="note">Không có quyền.</p>

  const value = field === 'description' ? description : shortDesc
  const setValue = (v: string) => {
    setDirty(true)
    setMsg('')
    if (field === 'description') setDescription(v)
    else setShortDesc(v)
  }

  function applyTool(open: string, close: string) {
    const el = areaRef.current
    if (!el) return
    const { selectionStart: s, selectionEnd: e } = el
    const next = value.slice(0, s) + open + value.slice(s, e) + close + value.slice(e)
    setValue(next)
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(s + open.length, e + open.length)
    })
  }

  async function onSave(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr('')
    setMsg('')
    try {
      const r = await api.saveKeyIntro(KEY_ID, {
        description,
        short_desc: shortDesc,
      })
      setDescription(r.intro.description)
      setShortDesc(r.intro.short_desc)
      setDirty(false)
      setMsg('Đã lưu.')
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Lỗi lưu')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="admin-intro" onSubmit={onSave}>
      <div className="admin-intro-head">
        <div>
          <p className="buykey-eyebrow">
            <i className="fa-solid fa-pen-to-square" aria-hidden /> Quản trị
          </p>
          <h1 className="page-title">Bài giới thiệu key</h1>
          <p className="muted">{name || '…'}</p>
        </div>
        <div className="admin-intro-actions">
          <Link className="btn btn-ghost" to="/mua-minecraft" target="_blank">
            <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden /> Xem trang
          </Link>
          <button className="btn btn-primary" type="submit" disabled={busy || !dirty}>
            <i className="fa-solid fa-floppy-disk" aria-hidden /> {busy ? 'Đang lưu…' : 'Lưu'}
          </button>
        </div>
      </div>

      {err ? <p className="note">{err}</p> : null}
      {msg ? (
        <p className="admin-intro-ok">
          <i className="fa-solid fa-circle-check" aria-hidden /> {msg}
        </p>
      ) : null}

      <div className="panel">
        <div className="admin-intro-bar">
          <div className="buykey-tablist admin-intro-fields" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={field === 'description'}
              className={`buykey-tab${field === 'description' ? ' active' : ''}`}
              onClick={() => setField('description')}
            >
              <i className="fa-solid fa-align-left" aria-hidden /> Mô tả chi tiết
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={field === 'short_desc'}
              className={`buykey-tab${field === 'short_desc' ? ' active' : ''}`}
              onClick={() => setField('short_desc')}
            >
              <i className="fa-solid fa-paragraph" aria-hidden /> Mô tả ngắn
            </button>
          </div>
          <div className="admin-intro-view">
            <button
              type="button"
              className={`btn btn-sm ${view === 'edit' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setView('edit')}
            >
              <i className="fa-solid fa-code" aria-hidden /> Soạn
            </button>
            <button
              type="button"
              className={`btn btn-sm ${view === 'preview' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setView('preview')}
            >
              <i className="fa-solid fa-eye" aria-hidden /> Xem trước
            </button>
          </div>
        </div>

        {view === 'edit' ? (
          <>
            <div className="admin-intro-tools">
              {TOOLS.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  className="admin-intro-tool"
                  title={t.label}
                  aria-label={t.label}
                  onClick={() => applyTool(t.wrap[0], t.wrap[1])}
                >
                  <i className={`fa-solid ${t.icon}`} aria-hidden />
                </button>
              ))}
            </div>
            <textarea
              ref={areaRef}
              className="admin-intro-area"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              spellCheck={false}
              rows={field === 'description' ? 24 : 10}
            />
          </>
        ) : (
          <div
            className="buykey-pane prose admin-intro-preview"
            dangerouslySetInnerHTML={{ __html: value || '<p class="muted">Trống</p>' }}
          />
        )}
      </div>
    </form>
  )
}
